// Klienti a kontakty (docs/SPEC.md 2.4): zod schémata vstupů a tvary odpovědí API.
import { z } from 'zod'
import { clientTypeSchema } from './enums'
import { addressSchema, phoneInputSchema } from './org'
import type { JobSummary } from './jobs'
import type { Attachment } from './attachments'

export const CLIENT_TAGS = ['no_job_90d'] as const
export type ClientTag = (typeof CLIENT_TAGS)[number]

export const createClientSchema = z.object({
  type: clientTypeSchema.default('person'),
  name: z.string().trim().min(2, 'Zadej jméno').max(160),
  phone: phoneInputSchema.optional().or(z.literal('')),
  email: z.string().trim().email('Zadej platný email').optional().or(z.literal('')),
  billingAddress: addressSchema.optional(),
  ico: z
    .string()
    .trim()
    .regex(/^\d{8}$/, 'IČO má 8 číslic')
    .optional()
    .or(z.literal('')),
  dic: z.string().trim().max(14).optional().or(z.literal('')),
  note: z.string().trim().max(2000).optional().or(z.literal('')),
  /** true = založit i přes nalezené možné duplicity (uživatel to potvrdil). */
  force: z.boolean().optional(),
})
export type CreateClientInput = z.infer<typeof createClientSchema>

export const updateClientSchema = createClientSchema.omit({ force: true }).partial()
export type UpdateClientInput = z.infer<typeof updateClientSchema>

export const CLIENT_SORTS = ['activity', 'name'] as const
export const listClientsQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  sort: z.enum(CLIENT_SORTS).default('activity'),
  tag: z.enum(CLIENT_TAGS).optional(),
})
export type ListClientsQuery = z.infer<typeof listClientsQuerySchema>

export interface ClientSummary {
  id: string
  type: 'person' | 'company'
  name: string
  phoneE164: string | null
  email: string | null
  city: string | null
  lastActivityAt: string | null
  openJobs: number
  tags: ClientTag[]
}

export const contactInputSchema = z.object({
  name: z.string().trim().min(2).max(160),
  phone: phoneInputSchema.optional().or(z.literal('')),
  email: z.string().trim().email().optional().or(z.literal('')),
  role: z.string().trim().max(80).optional().or(z.literal('')),
  isPrimary: z.boolean().optional(),
})
export type ContactInput = z.infer<typeof contactInputSchema>

export interface Contact {
  id: string
  clientId: string
  name: string
  phoneE164: string | null
  email: string | null
  role: string | null
  isPrimary: boolean
}

export interface TimelineEntry {
  id: string
  entityType: string
  entityId: string
  type: string
  payload: Record<string, unknown>
  actorUserId: string | null
  actorAi: boolean
  createdAt: string
}

export interface PromiseSummary {
  id: string
  text: string
  dueAt: string
  status: 'open' | 'done' | 'cancelled'
  jobId: string | null
  clientId: string
}

/** Při POST /v1/clients bez `force`: 409 conflict s možnými duplicitami v `details`. */
export interface ClientConflictDetails {
  candidates: Array<ClientSummary & { reason: 'phone' | 'name' }>
}

export const importClientRowSchema = z.object({
  name: z.string().trim().min(2).max(160),
  phone: z.string().trim().max(30).optional().or(z.literal('')),
  email: z.string().trim().max(160).optional().or(z.literal('')),
  address: z.string().trim().max(240).optional().or(z.literal('')),
  note: z.string().trim().max(2000).optional().or(z.literal('')),
})
export const importClientsSchema = z.object({
  rows: z.array(importClientRowSchema).min(1).max(2000),
})
export type ImportClientsInput = z.infer<typeof importClientsSchema>

export interface ImportClientsResult {
  created: number
  skipped: Array<{
    row: number
    name: string
    reason: 'duplicate_phone' | 'duplicate_name' | 'invalid'
    existingId?: string
  }>
}

/** GET /v1/ares/:ico (bezplatné API MF ČR) */
export interface AresCompany {
  ico: string
  name: string
  dic: string | null
  address: { street?: string; city?: string; zip?: string }
}

export interface ClientDetail extends ClientSummary {
  billingAddress: { street?: string; city?: string; zip?: string; country?: string } | null
  ico: string | null
  dic: string | null
  note: string | null
  createdAt: string
  contacts: Contact[]
  jobs: JobSummary[]
  promises: PromiseSummary[]
  timeline: TimelineEntry[]
  attachments: Attachment[]
}
